import { useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useInvalidateAutomationGuides } from "@/hooks/useAutomationGuides";
import { useInvalidateExistingAutomationGuides } from "@/hooks/useExistingAutomationGuides";
import { useInvalidateDashboard } from "@/hooks/useDashboardData";
import { logActivity } from "@/hooks/useActivityLog";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { useNavigate } from "react-router-dom";
import { createSubscribeCallback } from "@/lib/realtimeErrorHandler";
import type {
  PendingGuide,
  PendingDirectGuide,
} from "./usePendingGuidesPersistence";
import { translateToast } from "@/lib/i18n-toast";

// Type definitions for Realtime payloads
interface AutomationGuidePayload {
  id: string;
  sop_id: string | null;
  organization_id: string;
  process_log_id: string | null;
  verification_status: string | null;
}

interface GuideGenerationJobPayload {
  id: string;
  sop_id: string | null;
  status: string;
  error: string | null;
  result: { guideId?: string } | null;
}

/**
 * Type guard to validate automation_guides realtime payload
 * Logs unexpected structures for debugging without breaking the flow
 */
function isValidAutomationGuidePayload(
  payload: unknown,
): payload is AutomationGuidePayload {
  if (!payload || typeof payload !== "object") {
    console.warn(
      "[Realtime] automation_guides payload is not an object:",
      payload,
    );
    return false;
  }

  const p = payload as Record<string, unknown>;

  // Required field: id must be a string (UUID)
  if (typeof p.id !== "string") {
    console.warn(
      "[Realtime] automation_guides payload missing or invalid 'id':",
      payload,
    );
    return false;
  }

  // sop_id can be null or string
  if (p.sop_id !== null && typeof p.sop_id !== "string") {
    console.warn(
      "[Realtime] automation_guides payload has invalid 'sop_id' type:",
      typeof p.sop_id,
    );
    return false;
  }

  return true;
}

/**
 * Type guard to validate guide_generation_jobs realtime payload
 * Logs unexpected structures for debugging without breaking the flow
 */
function isValidGuideGenerationJobPayload(
  payload: unknown,
): payload is GuideGenerationJobPayload {
  if (!payload || typeof payload !== "object") {
    console.warn(
      "[Realtime] guide_generation_jobs payload is not an object:",
      payload,
    );
    return false;
  }

  const p = payload as Record<string, unknown>;

  // Required field: id must be a string (UUID)
  if (typeof p.id !== "string") {
    console.warn(
      "[Realtime] guide_generation_jobs payload missing or invalid 'id':",
      payload,
    );
    return false;
  }

  // Required field: status must be a string
  if (typeof p.status !== "string") {
    console.warn(
      "[Realtime] guide_generation_jobs payload missing or invalid 'status':",
      payload,
    );
    return false;
  }

  // sop_id can be null or string
  if (p.sop_id !== null && typeof p.sop_id !== "string") {
    console.warn(
      "[Realtime] guide_generation_jobs payload has invalid 'sop_id' type:",
      typeof p.sop_id,
    );
    return false;
  }

  // error can be null or string
  if (p.error !== null && typeof p.error !== "string") {
    console.warn(
      "[Realtime] guide_generation_jobs payload has invalid 'error' type:",
      typeof p.error,
    );
    return false;
  }

  // result can be null or object
  if (p.result !== null && typeof p.result !== "object") {
    console.warn(
      "[Realtime] guide_generation_jobs payload has invalid 'result' type:",
      typeof p.result,
    );
    return false;
  }

  return true;
}

interface UsePendingGuidesRealtimeOptions {
  enabled?: boolean;
  pendingGuidesRef: React.MutableRefObject<Map<string, PendingGuide>>;
  pendingDirectGuidesRef: React.MutableRefObject<
    Map<string, PendingDirectGuide>
  >;
  onGuideCompleted: (sopId: string, guideId: string, sopTitle: string) => void;
  onDirectGuideCompleted: (
    jobId: string,
    guideId: string,
    processName: string,
  ) => void;
  onGuideFailed: (sopId: string, error?: string, sopTitle?: string) => void;
  onDirectGuideFailed: (
    jobId: string,
    error?: string,
    processName?: string,
  ) => void;
}

export function usePendingGuidesRealtime({
  enabled = true,
  pendingGuidesRef,
  pendingDirectGuidesRef,
  onGuideCompleted,
  onDirectGuideCompleted,
  onGuideFailed,
  onDirectGuideFailed,
}: UsePendingGuidesRealtimeOptions) {
  const { t } = useTranslation();
  const invalidateGuides = useInvalidateAutomationGuides();
  const invalidateExistingGuides = useInvalidateExistingAutomationGuides();
  const invalidateDashboard = useInvalidateDashboard();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Subscribe to realtime updates on automation_guides table
  // When a new guide is created for a pending SOP, show notification
  useEffect(() => {
    if (!enabled) return;

    const channel = supabase
      .channel("pending-guides")
      .on(
        "postgres_changes",
        {
          event: "*", // Listen for INSERT (new guide) and UPDATE (regenerated guide via upsert)
          schema: "public",
          table: "automation_guides",
        },
        async (payload) => {
          // Validate payload structure before processing
          if (!isValidAutomationGuidePayload(payload.new)) {
            return; // Type guard already logged the issue
          }

          const newGuide = payload.new;
          const sopId = newGuide.sop_id;

          // Only show guide when verification is complete
          // Guides are saved with verification_status='pending' initially
          if (
            newGuide.verification_status !== "completed" &&
            newGuide.verification_status !== "skipped"
          ) {
            console.log(
              `[Realtime] Guide ${newGuide.id} not ready yet (verification_status: ${newGuide.verification_status})`,
            );
            return;
          }

          // Use ref to get current pending guides (avoids stale closure)
          const currentPending = pendingGuidesRef.current;
          if (sopId && currentPending.has(sopId)) {
            const pending = currentPending.get(sopId)!;

            // Log activity
            await logActivity({
              action: "automation_guide_generated",
              resourceType: "automation_guide",
              resourceId: newGuide.id,
              resourceName: pending.sopTitle,
            });

            // Clear pending state
            onGuideCompleted(sopId, newGuide.id, pending.sopTitle);

            // Invalidate caches
            invalidateGuides();
            invalidateExistingGuides();
            invalidateDashboard();

            // Show success toast with action button to view guide
            toast({
              title: translateToast("guides.ready"),
              description: translateToast("guides.readyDesc", {
                title: pending.sopTitle,
              }),
              action: (
                <ToastAction
                  altText={t("guides.viewGuide")}
                  onClick={() =>
                    navigate(`/automation-guides?id=${newGuide.id}`)
                  }
                >
                  {t("guides.viewGuide")}
                </ToastAction>
              ),
              duration: 10000,
            });
          }
        },
      )
      .subscribe(createSubscribeCallback("pending-guides-automation"));

    return () => {
      supabase.removeChannel(channel);
    };
  }, [
    enabled,
    pendingGuidesRef,
    onGuideCompleted,
    invalidateGuides,
    invalidateExistingGuides,
    invalidateDashboard,
    toast,
    navigate,
  ]);

  // Subscribe to realtime updates on guide_generation_jobs table
  // When a job fails, show error notification
  // When a job completes for a direct guide (no SOP), handle the completion
  useEffect(() => {
    if (!enabled) return;

    const channel = supabase
      .channel("guide-generation-jobs")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "guide_generation_jobs",
        },
        async (payload) => {
          // Validate payload structure before processing
          if (!isValidGuideGenerationJobPayload(payload.new)) {
            return; // Type guard already logged the issue
          }

          const job = payload.new;
          const jobId = job.id;
          const sopId = job.sop_id;
          const status = job.status;
          const guideId = job.result?.guideId; // Stored in result JSONB field when job completes successfully

          // Check if this is a direct guide job (tracked by jobId)
          const currentDirectPending = pendingDirectGuidesRef.current;
          if (jobId && currentDirectPending.has(jobId)) {
            const pending = currentDirectPending.get(jobId)!;

            if (status === "completed" && guideId) {
              // Direct guide completed successfully

              // Log activity
              await logActivity({
                action: "direct_automation_guide_generated",
                resourceType: "automation_guide",
                resourceId: guideId,
                resourceName: pending.processName,
              });

              // Clear pending state and show toast/redirect
              onDirectGuideCompleted(jobId, guideId, pending.processName);

              // Invalidate caches
              invalidateGuides();
              invalidateExistingGuides();
              invalidateDashboard();

              // Show success toast with action button to view guide
              toast({
                title: translateToast("guides.ready"),
                description: translateToast("guides.readyDesc", {
                  title: pending.processName,
                }),
                action: (
                  <ToastAction
                    altText={t("guides.viewGuide")}
                    onClick={() => navigate(`/automation-guides?id=${guideId}`)}
                  >
                    {t("guides.viewGuide")}
                  </ToastAction>
                ),
                duration: 10000,
              });
            } else if (status === "failed") {
              // Direct guide failed
              onDirectGuideFailed(
                jobId,
                job.error ?? undefined,
                pending.processName,
              );

              // Show error toast
              toast({
                title: translateToast("guides.generationFailed"),
                description:
                  job.error ||
                  t("guides.generationFailedDesc", {
                    title: pending.processName,
                  }),
                variant: "destructive",
              });
            }
            return; // Already handled as direct guide
          }

          // Handle SOP-based guides (legacy path)
          const currentPending = pendingGuidesRef.current;
          if (sopId && currentPending.has(sopId)) {
            const pending = currentPending.get(sopId)!;

            if (status === "failed") {
              onGuideFailed(sopId, job.error ?? undefined, pending.sopTitle);

              // Show error toast
              toast({
                title: translateToast("guides.generationFailed"),
                description:
                  job.error ||
                  t("guides.generationFailedDesc", { title: pending.sopTitle }),
                variant: "destructive",
              });
            }
            // Note: 'completed' status for SOP guides is handled by the automation_guides INSERT subscription
          }
        },
      )
      .subscribe(createSubscribeCallback("pending-guides-jobs"));

    return () => {
      supabase.removeChannel(channel);
    };
  }, [
    enabled,
    pendingGuidesRef,
    pendingDirectGuidesRef,
    onGuideFailed,
    onDirectGuideCompleted,
    onDirectGuideFailed,
    invalidateGuides,
    invalidateExistingGuides,
    invalidateDashboard,
    toast,
    navigate,
    t,
  ]);
}

// Fallback check - calls check-automation-guide-job once to ensure OpenAI job gets finalized
// This handles the case where realtime subscription missed the database update
export function useFallbackCheck(
  pendingGuidesRef: React.MutableRefObject<Map<string, PendingGuide>>,
  onComplete: (sopId: string, guideId: string, sopTitle?: string) => void,
  onFailed: (sopId: string, error?: string, sopTitle?: string) => void,
) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const performFallbackCheck = useCallback(
    async (jobId: string, sopId: string) => {
      // Use ref to get current pending guides (avoids stale closure)
      const currentPending = pendingGuidesRef.current;

      // Skip if no longer pending (realtime already handled it)
      if (!currentPending.has(sopId)) return;

      const pending = currentPending.get(sopId);

      try {
        const session = await supabase.auth.getSession();
        const accessToken = session.data.session?.access_token;
        if (!accessToken) return;

        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const response = await fetch(
          `${supabaseUrl}/functions/v1/check-automation-guide-job`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
              apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({ jobId }),
          },
        );

        const result = await response.json();

        // Handle completed status directly (don't rely on realtime)
        if (result.status === "completed" && result.guideId) {
          onComplete(sopId, result.guideId, pending?.sopTitle);
        } else if (result.status === "failed") {
          onFailed(sopId, result.error, pending?.sopTitle);
          toast({
            title: translateToast("guides.generationFailed"),
            description:
              result.error ||
              t("guides.generationFailedDesc", { title: pending?.sopTitle }),
            variant: "destructive",
          });
        }
        // If still running, realtime subscription will handle completion
      } catch (error) {
        console.error("Fallback check failed:", error);
      }
    },
    [pendingGuidesRef, onComplete, onFailed, toast, t],
  );

  return { performFallbackCheck };
}

// Fallback check for direct guides (no SOP) - same pattern as above
// Handles the case where mobile browsers suspend WebSocket connections
export function useDirectFallbackCheck(
  pendingDirectGuidesRef: React.MutableRefObject<
    Map<string, PendingDirectGuide>
  >,
  onComplete: (jobId: string, guideId: string, processName: string) => void,
  onFailed: (jobId: string, error?: string, processName?: string) => void,
  invalidateGuides: () => void,
  invalidateExistingGuides: () => void,
  invalidateDashboard: () => void,
) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();

  const performDirectFallbackCheck = useCallback(
    async (jobId: string) => {
      // Use ref to get current pending guides (avoids stale closure)
      const currentPending = pendingDirectGuidesRef.current;

      // Skip if no longer pending (realtime already handled it)
      if (!currentPending.has(jobId)) return;

      const pending = currentPending.get(jobId);

      try {
        const session = await supabase.auth.getSession();
        const accessToken = session.data.session?.access_token;
        if (!accessToken) return;

        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const response = await fetch(
          `${supabaseUrl}/functions/v1/check-automation-guide-job`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
              apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({ jobId }),
          },
        );

        const result = await response.json();

        // Handle completed status directly (don't rely on realtime)
        if (result.status === "completed" && result.guideId) {
          // Clear pending state
          onComplete(jobId, result.guideId, pending?.processName || "");

          // Invalidate caches
          invalidateGuides();
          invalidateExistingGuides();
          invalidateDashboard();

          // Show success toast with action button to view guide
          toast({
            title: translateToast("guides.ready"),
            description: translateToast("guides.readyDesc", {
              title: pending?.processName,
            }),
            action: (
              <ToastAction
                altText={t("guides.viewGuide")}
                onClick={() =>
                  navigate(`/automation-guides?id=${result.guideId}`)
                }
              >
                {t("guides.viewGuide")}
              </ToastAction>
            ),
            duration: 10000,
          });
        } else if (result.status === "failed") {
          onFailed(jobId, result.error, pending?.processName);
          toast({
            title: translateToast("guides.generationFailed"),
            description:
              result.error ||
              t("guides.generationFailedDesc", {
                title: pending?.processName,
              }),
            variant: "destructive",
          });
        }
        // If still running, realtime subscription will handle completion
      } catch (error) {
        console.error("Direct fallback check failed:", error);
      }
    },
    [
      pendingDirectGuidesRef,
      onComplete,
      onFailed,
      invalidateGuides,
      invalidateExistingGuides,
      invalidateDashboard,
      toast,
      navigate,
      t,
    ],
  );

  return { performDirectFallbackCheck };
}
